import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronRight,
  PartyPopper,
  Calendar,
  Users,
  MapPin,
  Armchair,
  Shirt,
  UsersRound,
  ListTodo,
  Gift,
  Globe,
  Sparkles,
  BookOpen,
  Upload,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { AppBreadcrumbs } from '@/components/layout/AppBreadcrumbs';
import { PageContainer } from '@/components/layout/PageContainer';

interface HowToSection {
  icon: React.ReactNode;
  title: string;
  emoji: string;
  steps: string[];
  tips?: string[];
  where?: string;
}

const sections: HowToSection[] = [
  {
    icon: <PartyPopper className="h-5 w-5" />,
    title: 'Create a Celebration',
    emoji: '🎉',
    where: 'Home → New Celebration',
    steps: [
      'Click "New Celebration" on your dashboard (or hit the + button).',
      'Pick a type — Wedding, Birthday, Anniversary, Corporate, or anything else.',
      'Give it a name, a short description, and set the date range.',
      "That's it! You'll land on your celebration dashboard where everything lives.",
    ],
    tips: [
      "You can also chat with our AI assistant to create a celebration — just tell it what you're planning and it'll set things up for you.",
      "Don't worry about getting everything perfect now. You can always edit later from Settings.",
    ],
  },
  {
    icon: <Calendar className="h-5 w-5" />,
    title: 'Add Events to Your Celebration',
    emoji: '📅',
    where: 'Celebration Dashboard → Events tab → + New Event',
    steps: [
      'Open your celebration and go to the Events tab.',
      'Click "+ New Event" — give it a title, pick a date & time.',
      "Optionally pick a venue (start typing and we'll suggest places from Google).",
      'If your venue has rooms, pick the room and a seating layout.',
      'Hit Save — your event is live!',
    ],
    tips: [
      'A "celebration" is the big picture (e.g., "Sarah\'s Wedding"), and "events" are the individual parts (ceremony, reception, rehearsal dinner, etc.).',
      'You can create as many events as you need under one celebration.',
    ],
  },
  {
    icon: <Upload className="h-5 w-5" />,
    title: 'Import Your Contacts',
    emoji: '📇',
    where: 'Top nav → Contacts',
    steps: [
      'Go to Contacts from the top navigation bar.',
      'Click "Import CSV" to bulk-upload your guest list.',
      "Upload a spreadsheet — we'll auto-detect columns like Name, Email, Phone.",
      "Map any columns we couldn't figure out, then hit Import.",
      'Your contacts are saved and ready to be invited to any celebration!',
    ],
    tips: [
      'You can also add contacts one by one with the "+ Add" button.',
      'The CSV just needs a name and email at minimum. Phone, relationship, and other fields are optional.',
    ],
  },
  {
    icon: <Users className="h-5 w-5" />,
    title: 'Invite Guests to a Celebration',
    emoji: '💌',
    where: 'Contacts page → select a celebration → check the boxes',
    steps: [
      'Go to Contacts and pick a celebration from the dropdown at the top.',
      "You'll see checkboxes next to each contact — check the ones you want to invite.",
      'Click "Add to Celebration" — they\'re now on your guest list!',
      'To remove someone, just uncheck them and save.',
    ],
    tips: [
      "If you only have one celebration, it's auto-selected for you.",
      'This adds people to the celebration level. You can then fine-tune which specific events they attend (see next section).',
    ],
  },
  {
    icon: <Calendar className="h-5 w-5" />,
    title: 'Assign Guests to Specific Events',
    emoji: '✅',
    where: 'Celebration → Guests tab → Events sub-tab',
    steps: [
      'Open your celebration and go to the Guests tab.',
      'Switch to the "Events" sub-tab — you\'ll see a grid of guests × events.',
      'Check the box where a guest row meets an event column to include them.',
      'Use "Select All" at the top of a column to invite everyone to that event.',
      "Hit Save when you're done.",
    ],
    tips: [
      'This is how you handle things like "Everyone comes to the reception, but only family comes to the ceremony."',
      'You can also set how many extra guests (+1, +2, etc.) each person can bring.',
    ],
  },
  {
    icon: <MapPin className="h-5 w-5" />,
    title: 'Manage Venues & Rooms',
    emoji: '🏛️',
    where: 'Top nav → Celebrations → Venues, or from an Event form',
    steps: [
      'Browse venues from the Venues page — we pull details from Google Places.',
      'Click "Rooms & Details" on any venue to see or add rooms/spaces.',
      'Add rooms manually (name, type, capacity, seating config), or use "Auto-Detect" to scan the venue\'s website.',
      'When creating an event, search for a venue — once selected, its rooms appear in a dropdown.',
      'Pick a room and seating layout for the event.',
    ],
    tips: [
      'Room types include Ballroom, Garden, Rooftop, Conference Room, Chapel, and more.',
      'Seating configs like Theater, Banquet, U-Shape, and Classroom help plan the layout.',
    ],
  },
  {
    icon: <Armchair className="h-5 w-5" />,
    title: 'Set Up Seating',
    emoji: '💺',
    where: 'Event → Seating Setup (from event details page)',
    steps: [
      'Open an event and click "Seating Setup."',
      "If no room is assigned yet, you'll be asked to pick one first.",
      'Set layout parameters — rows, columns, spacing — for your arrangement.',
      'Click "Generate Layout" to create the seat map.',
      'Now go to Event → Guests → Seating tab to assign people to seats.',
      'Drag a guest from the sidebar and drop them onto a seat — or click a seat, then click a guest.',
    ],
    tips: [
      'Arrangement types: Grid (rows & columns), Rows (long tables), Single Row (one line), U-Shape (horseshoe), and Scatter (free-form).',
      'Use the zoom controls to get a closer look at large layouts.',
      'You can search for a specific guest in the sidebar.',
    ],
  },
  {
    icon: <Shirt className="h-5 w-5" />,
    title: 'Set a Dress Code',
    emoji: '👔',
    where: 'Event details → Dress Code button',
    steps: [
      'Open an event and click the "Dress Code" button.',
      'Toggle dress code ON for that event.',
      "You'll see a grid: rows are gender (Male, Female, Other) and columns are age groups (Adult, Teen, Child, Infant).",
      'Type what guests should wear in each cell (e.g., "Black tie" for Male Adults, "Cocktail dress" for Female Adults).',
      'Use "Copy to All" to fill every cell with the same thing.',
      'Or click "Use Template" to pick from pre-made suggestions based on your celebration type.',
      'Hit "Save All" when done.',
    ],
    tips: [
      'Guests see their personalized dress code on their invitation page — they only see what applies to their gender and age.',
      'Templates are filtered based on your celebration tags (wedding, formal, outdoor, etc.).',
    ],
  },
  {
    icon: <UsersRound className="h-5 w-5" />,
    title: 'Build Your Team',
    emoji: '🤝',
    where: 'Celebration → Team tab, or Celebration Settings → Team Members section',
    steps: [
      'Open your celebration and go to the Team tab.',
      'Click "Add Team Member" — search for a registered user by name or email.',
      'Pick their role:',
      '   • Organizer — full access to everything',
      '   • Event Manager — manages events and guests',
      '   • Vendor Coordinator — handles vendor relationships',
      '   • Member — view-only access',
      'Click Add — they can now collaborate on this celebration!',
    ],
    tips: [
      "You can also add team members from the celebration's Settings/Edit page.",
      'Each role has a description so you know exactly what access they get.',
    ],
  },
  {
    icon: <ListTodo className="h-5 w-5" />,
    title: 'Manage Tasks',
    emoji: '📋',
    where: 'Celebration → Tasks tab, or Event → Tasks',
    steps: [
      'Open your celebration (or a specific event) and go to the Tasks tab.',
      'Click "+ New Task" — give it a title, description, due date, and assign it to a team member.',
      'Set the priority (High, Medium, Low) and status (Pending, In Progress, Done).',
      'Track progress from the task board — filter by status, assignee, or priority.',
    ],
    tips: [
      'Tasks can live at the celebration level (general to-dos) or the event level (event-specific prep).',
      'Every status change is logged in the activity feed.',
    ],
  },
  {
    icon: <Gift className="h-5 w-5" />,
    title: 'Set Up Gift Registries',
    emoji: '🎁',
    where: 'Celebration → Registries tab',
    steps: [
      'Open your celebration and go to the Registries tab.',
      'Click "Add Registry" — enter the store name and the link to your registry.',
      'Add as many registries as you like (Amazon, Target, Zola, etc.).',
      'These links will appear on your Guest Landing Page for guests to see.',
    ],
  },
  {
    icon: <Globe className="h-5 w-5" />,
    title: 'Design Your Guest Landing Page',
    emoji: '🌐',
    where: 'Celebration → Guest Landing Page',
    steps: [
      'Open your celebration and click "Guest Landing Page" (or find it in the sidebar).',
      'Pick a theme template — Elegant, Modern, Festive, or Minimal.',
      'Upload a hero banner image (wide images work best, around 1200×400).',
      'Write a welcome message — use {guest_name} to personalize it for each guest.',
      "Your landing page URL is automatically generated with your celebration's slug.",
      "Share the link with guests — they'll see their personalized invitation, dress code, RSVP buttons, venue details, and gift registries all in one place.",
    ],
    tips: [
      "The page is public — guests don't need an account to view it.",
      'Each guest gets a unique token in their link for personalization.',
    ],
  },
  {
    icon: <Sparkles className="h-5 w-5" />,
    title: 'Use the AI Assistant',
    emoji: '🤖',
    where: 'Chat (top nav) or the floating AI button',
    steps: [
      'Click "Chat" in the navigation bar, or tap the floating AI button on any page.',
      'Just tell it what you need in plain English: "Plan a birthday party for 50 people" or "What do I still need to do for Sarah\'s wedding?"',
      'The AI can create celebrations, suggest events, help with timelines, and answer questions about your plans.',
      "Everything it creates shows up in your real dashboard — it's not just a chatbot, it actually does the work.",
    ],
  },
];

export const HowToPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(['howto']);
  const [openSection, setOpenSection] = useState<number | null>(null);

  return (
    <PageContainer width="wide" className="space-y-6 animate-in fade-in duration-500">
      <AppBreadcrumbs />

      {/* Header */}
      <div className="text-center space-y-3 pb-4">
        <h1 className="text-3xl font-bold tracking-tight">
          {t('howto:page.titlePrefix')}{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-celebration-purple">
            curvato.us
          </span>
        </h1>
        <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
          {t('howto:page.subtitle')}
        </p>
      </div>

      {/* Quick Start Banner */}
      <Card className="bg-gradient-to-r from-primary/5 to-celebration-purple/5 border-primary/20">
        <CardContent className="py-5">
          <div className="flex items-start gap-3">
            <BookOpen className="h-6 w-6 text-primary mt-0.5 flex-shrink-0" />
            <div>
              <h2 className="font-semibold text-lg mb-1">{t('howto:page.quickStart.title')}</h2>
              <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                <li>
                  <strong>Create a celebration</strong> &mdash; {t('howto:page.quickStart.steps.step1')}
                </li>
                <li>
                  <strong>Import your contacts</strong> &mdash; {t('howto:page.quickStart.steps.step2')}
                </li>
                <li>
                  <strong>Add events</strong> &mdash; {t('howto:page.quickStart.steps.step3')}
                </li>
                <li>
                  <strong>Invite guests</strong> &mdash; {t('howto:page.quickStart.steps.step4')}
                </li>
              </ol>
              <p className="text-sm text-muted-foreground mt-2">
                {t('howto:page.quickStart.note')}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Accordion Sections */}
      <div className="space-y-3">
        {sections.map((section, index) => (
          <Card
            key={index}
            className={`transition-all duration-200 ${openSection === index ? 'ring-1 ring-primary/30 shadow-md' : 'hover:shadow-sm'}`}
          >
            <button
              onClick={() => setOpenSection(openSection === index ? null : index)}
              className="w-full px-5 py-4 flex items-center gap-3 text-left"
            >
              <span className="text-2xl">{section.emoji}</span>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-base">{section.title}</h3>
                {section.where && (
                  <p className="text-xs text-muted-foreground mt-0.5">📍 {section.where}</p>
                )}
              </div>
              {openSection === index ? (
                <ChevronDown className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              ) : (
                <ChevronRight className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              )}
            </button>

            {openSection === index && (
              <CardContent className="pt-0 pb-5 px-5 animate-in slide-in-from-top-2 duration-200">
                <div className="border-t pt-4 space-y-4">
                  {/* Steps */}
                  <ol className="space-y-2">
                    {section.steps.map((step, stepIndex) => (
                      <li key={stepIndex} className="flex gap-3 text-sm">
                        <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold flex items-center justify-center mt-0.5">
                          {stepIndex + 1}
                        </span>
                        <span className="text-muted-foreground leading-relaxed">{step}</span>
                      </li>
                    ))}
                  </ol>

                  {/* Tips */}
                  {section.tips && section.tips.length > 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 space-y-1.5">
                      <p className="text-xs font-semibold text-amber-800 uppercase tracking-wider">
                        💡 {t('howto:page.tips')}
                      </p>
                      {section.tips.map((tip, tipIndex) => (
                        <p key={tipIndex} className="text-sm text-amber-900/80">
                          {tip}
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {/* Footer */}
      <div className="text-center py-6 space-y-3">
        <p className="text-sm text-muted-foreground">
          {t('howto:page.footer.message')}
        </p>
        <Button variant="outline" onClick={() => navigate('/chat')}>
          <Sparkles className="h-4 w-4 mr-1.5" />
          {t('howto:page.footer.chatButton')}
        </Button>
      </div>
    </PageContainer>
  );
};
